import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Button, Drawer, Dropdown, Image, Layout, Menu, Space } from 'antd'
import { NavLink, useHistory } from 'react-router-dom'
import { LoginOutlined, MenuOutlined, PoweroffOutlined, UserAddOutlined, UserOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentDots } from '@fortawesome/free-solid-svg-icons'

import ProfileAvatar from '../ProfileAvatar/ProfileAvatar'

import { useAuthContext } from '../../../context/auth'
import { useExpertsContext } from '../../../context/experts'
import { useSearchContext } from '../../../context/search'
import { useChatContext } from '../../../context/chat'
import { IntegrationTypes, TChat } from '../../../context/types'

import logo from '../../../images/icons/logo.png'

import { RoutePath } from '../../routes'
import { currentBrand } from '../../../config'
import './header.less'
import { useIntegrationContext } from '../../../context/integration'
import Search from '../Search'
import CustomLink from '../CustomLink/CustomLink'
import { usePageLeaveContext } from '../../../context/pageLeave'
import { useChannelContext } from '../../../context/channel'

const { Header: AntHeader } = Layout

function Header() {
	const [doLogout, setDoLogout] = useState<boolean>(false)
	const { auth, actions } = useAuthContext()
	const { actions: expertsActions } = useExpertsContext()
	const { actions: searchActions } = useSearchContext()
	const { chat } = useChatContext()
	const { integration } = useIntegrationContext()
	const { channel } = useChannelContext()
	const landingUrl = useMemo(
		() => (integration.type === IntegrationTypes.embed ? '/' : currentBrand.landingUrl || '/'),
		[integration.type]
	)

	const history = useHistory()
	const { pageLeave, actions: pageLeaveActions } = usePageLeaveContext()

	const [openMenu, setOpenMenu] = useState<boolean>(false)

	const goToLoginAction = () => history.push(RoutePath.Login)
	const goToRegisterAction = () => history.push(RoutePath.Register)
	const goToProfileAction = () => history.push(RoutePath.Profile)
	const goToMessages = () => history.push(RoutePath.Messages)
	const goToExpertsAction = (e: any) => {
		e.preventDefault()
		searchActions.setValue('')
		expertsActions.getExperts({ channel: channel.selectedChannelId }, null, true)
		setOpenMenu(false)
	}

	const cbHandlers: { [cbName: string]: (e?: any) => void } = {
		goToExpertsAction,
	}

	useEffect(() => {
		if (doLogout && !pageLeave.condition) {
			actions.logout()
			setDoLogout(false)
		}
	}, [doLogout, pageLeave.condition])

	const unreadMessagesCount = chat.chats.reduce((count, c: TChat) => count + c.unreadMessagesCount, 0)

	const handleLogout = () => {
		if (pageLeave.condition) {
			pageLeaveActions.showConfirm(() => {
				pageLeaveActions.setConditions({})
				setDoLogout(true)
			})
		} else {
			actions.logout()
		}
	}

	const profileMenu = (
		<Menu>
			<Menu.Item icon={<UserOutlined />} onClick={goToProfileAction}>
				Profile
			</Menu.Item>
			<Menu.Item icon={<PoweroffOutlined />} onClick={handleLogout}>
				Logout
			</Menu.Item>
		</Menu>
	)
	const authMenu = (
		<Menu>
			<Menu.Item icon={<LoginOutlined />} onClick={goToLoginAction}>
				Login
			</Menu.Item>
			<Menu.Item icon={<UserAddOutlined />} onClick={goToRegisterAction}>
				Register
			</Menu.Item>
		</Menu>
	)
	const menu = (
		<menu className="header-menu">
			{currentBrand.headerLinks.map(({ name, cbName, ...link }) => (
				<CustomLink {...link} key={name} onClick={cbName && cbHandlers[cbName]}>
					{name}
				</CustomLink>
			))}
			{auth.token && (
				<Badge count={unreadMessagesCount} className="header-menu-messages" size="small">
					<NavLink to="/messages" activeClassName="header-active" onClick={() => setOpenMenu(false)}>
						Messages
					</NavLink>
				</Badge>
			)}
		</menu>
	)

	type TActionsComponentProps = {
		signupText?: string
	}

	const ActionsComponent = (props: TActionsComponentProps) =>
		!auth.token ? (
			<>
				<div className="header-menu-hide">
					<Space className="header-actions">
						<NavLink to="/login" activeClassName="header-active">
							Login
						</NavLink>
						<Button type="primary" shape="round" className="btn-white" onClick={goToRegisterAction}>
							{props.signupText || 'Register'}
						</Button>
					</Space>
				</div>
				<div className="header-menu-show">
					<Dropdown overlay={authMenu} trigger={['click']} placement="bottomRight">
						<Button icon={<UserAddOutlined />} type="primary" shape="circle" />
					</Dropdown>
				</div>
			</>
		) : (
			<div className="header-actions">
				<Badge count={unreadMessagesCount} className="header-menu-messages-icon" size="small">
					<FontAwesomeIcon className="header-actions-chat-icon" icon={faCommentDots} onClick={goToMessages} />
				</Badge>
				<Dropdown overlay={profileMenu} trigger={['click']} placement="bottomRight">
					<ProfileAvatar className="header-avatar" size="large" src={auth.user?.profile_pic_url} />
				</Dropdown>
			</div>
		)

	if (integration?.config?.header === 'Slim') {
		return (
			<AntHeader className="header no-user-select slim-header">
				<div className="header-content">
					<CustomLink to={landingUrl} className="header-logo-link">
						<Image src={currentBrand.logoUrl || logo} preview={false} referrerPolicy="no-referrer" />
					</CustomLink>
					<Search withoutButton />
					<ActionsComponent signupText="Sign Up" />
				</div>
				<Drawer
					title={
						<CustomLink to={landingUrl}>
							<Image src={currentBrand.logoUrl || logo} preview={false} referrerPolicy="no-referrer" />
						</CustomLink>
					}
					className="header-drawer"
					placement="left"
					onClose={() => setOpenMenu(false)}
					visible={openMenu}
					children={menu}
				/>
			</AntHeader>
		)
	}

	return (
		<AntHeader className="header no-user-select">
			<div className="header-content">
				<Button
					icon={<MenuOutlined />}
					type="link"
					className="header-menu-btn header-menu-show"
					onClick={() => setOpenMenu(true)}
				/>
				<CustomLink to={landingUrl} target="_blank" className="header-logo-link">
					<Image src={currentBrand.logoUrl || logo} preview={false} referrerPolicy="no-referrer" />
				</CustomLink>
				<div className="header-menu-hide">{menu}</div>
				<ActionsComponent />
			</div>
			<Drawer
				title={
					<CustomLink to={landingUrl}>
						<Image src={currentBrand.logoUrl || logo} preview={false} referrerPolicy="no-referrer" />
					</CustomLink>
				}
				className="header-drawer"
				placement="left"
				onClose={() => setOpenMenu(false)}
				visible={openMenu}
				children={menu}
			/>
		</AntHeader>
	)
}

export default Header
